'use client';

import React, { useState, useEffect, useMemo, useCallback, CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import styles from './DateRangePicker.module.css';

export type DateRangeMode = 'date' | 'months' | 'alltime';

export interface DateRange {
    from: string;
    to: string;
}

export interface DateRangePreset {
    label: string;
    getRange: () => DateRange;
}

export interface DateRangePickerProps {
    from?: string;
    to?: string;
    onChange: (range: DateRange) => void;
    presets?: DateRangePreset[];
    modes?: DateRangeMode[];
    modeLabels?: Partial<Record<DateRangeMode, string>>;
}

const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const parseDate = (str: string): Date | null => {
    if (!str) return null;
    const dateOnly = str.includes('T') ? str.split('T')[0] : str;
    const d = new Date(dateOnly + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
};

type SelectionPhase = 'start' | 'end';
type RailVars = CSSProperties & { ['--index']?: number; ['--count']?: number };

const DEFAULT_MODES: DateRangeMode[] = ['date', 'months', 'alltime'];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    from = '',
    to = '',
    onChange,
    presets,
    modes = DEFAULT_MODES,
    modeLabels,
}) => {
    const t = useTranslations('dateRangePicker');

    const activeModes = modes.length > 0 ? modes : DEFAULT_MODES;
    const defaultModeLabels: Record<DateRangeMode, string> = {
        date: t('modeDate'),
        months: t('modeMonths'),
        alltime: t('modeAllTime'),
    };
    const mergedLabels = { ...defaultModeLabels, ...modeLabels };

    const monthNames = [
        t('months.january'), t('months.february'), t('months.march'),
        t('months.april'), t('months.may'), t('months.june'),
        t('months.july'), t('months.august'), t('months.september'),
        t('months.october'), t('months.november'), t('months.december'),
    ];

    const weekDays = [
        t('weekDays.su'), t('weekDays.mo'), t('weekDays.tu'),
        t('weekDays.we'), t('weekDays.th'), t('weekDays.fr'),
        t('weekDays.sa'),
    ];

    const activePresets = presets || [
        {
            label: t('today'),
            getRange: () => {
                const d = new Date();
                return { from: formatDate(d), to: formatDate(d) };
            }
        },
        {
            label: t('yesterday'),
            getRange: () => {
                const d = new Date();
                d.setDate(d.getDate() - 1);
                return { from: formatDate(d), to: formatDate(d) };
            }
        },
        {
            label: t('thisWeek'),
            getRange: () => {
                const now = new Date();
                const day = now.getDay();
                const start = new Date(now);
                start.setDate(now.getDate() - day);
                return { from: formatDate(start), to: formatDate(now) };
            }
        },
        {
            label: t('thisMonth'),
            getRange: () => {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                return { from: formatDate(start), to: formatDate(now) };
            }
        },
        {
            label: t('allTime'),
            getRange: () => ({ from: '', to: '' })
        },
    ];

    const [mode, setMode] = useState<DateRangeMode>(activeModes[0]);
    const [monthCount, setMonthCount] = useState<number>(3);
    const [startDate, setStartDate] = useState<Date | null>(parseDate(from));
    const [endDate, setEndDate] = useState<Date | null>(parseDate(to));
    const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
    const [phase, setPhase] = useState<SelectionPhase>('start');
    const [currentMonth, setCurrentMonth] = useState<Date>(() => {
        const parsed = parseDate(from);
        return parsed || new Date();
    });

    const modeIndex = activeModes.indexOf(mode);

    useEffect(() => {
        setStartDate(parseDate(from));
        setEndDate(parseDate(to));
    }, [from, to]);

    const handleModeChange = (newMode: DateRangeMode) => {
        setMode(newMode);
        if (newMode === 'alltime') {
            setStartDate(null);
            setEndDate(null);
            setPhase('start');
            onChange({ from: '', to: '' });
        } else if (newMode === 'months') {
            applyMonthRange(monthCount);
        }
    };

    const applyMonthRange = (count: number) => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - count);
        setStartDate(start);
        setEndDate(end);
        setPhase('start');
        onChange({ from: formatDate(start), to: formatDate(end) });
    };

    const handleMonthCountChange = (count: number) => {
        const clamped = Math.max(1, Math.min(24, count));
        setMonthCount(clamped);
        applyMonthRange(clamped);
    };

    const daysInMonth = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startingDayOfWeek = firstDay.getDay();
        const daysCount = lastDay.getDate();

        const days: (Date | null)[] = [];
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysCount; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    }, [currentMonth]);

    const isSameDay = (a: Date | null, b: Date | null) => {
        if (!a || !b) return false;
        return a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth()
            && a.getDate() === b.getDate();
    };

    const isInRange = useCallback((date: Date) => {
        if (!startDate) return false;
        const end = phase === 'end' && hoveredDate ? hoveredDate : endDate;
        if (!end) return false;

        const lo = startDate <= end ? startDate : end;
        const hi = startDate <= end ? end : startDate;
        return date >= lo && date <= hi;
    }, [startDate, endDate, hoveredDate, phase]);

    const isToday = (date: Date) => {
        const now = new Date();
        return date.getFullYear() === now.getFullYear()
            && date.getMonth() === now.getMonth()
            && date.getDate() === now.getDate();
    };

    const handleDayClick = (date: Date) => {
        if (phase === 'start') {
            setStartDate(date);
            setEndDate(null);
            setPhase('end');
        } else {
            let s = startDate!;
            let e = date;
            if (e < s) {
                [s, e] = [e, s];
            }
            setStartDate(s);
            setEndDate(e);
            setPhase('start');
            onChange({ from: formatDate(s), to: formatDate(e) });
        }
    };

    const handlePreset = (preset: DateRangePreset) => {
        const range = preset.getRange();
        const parsedFrom = parseDate(range.from);
        const parsedTo = parseDate(range.to);
        setStartDate(parsedFrom);
        setEndDate(parsedTo);
        setPhase('start');
        if (parsedTo) {
            setCurrentMonth(new Date(parsedTo.getFullYear(), parsedTo.getMonth()));
        }
        onChange(range);
    };

    const previousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const activePresetIndex = activePresets.findIndex(p => {
        const r = p.getRange();
        const rFrom = r.from ? parseDate(r.from) : null;
        const rTo = r.to ? parseDate(r.to) : null;
        if (!rFrom && !rTo && !startDate && !endDate) return true;
        if (!rFrom || !rTo || !startDate || !endDate) return false;
        return isSameDay(rFrom, startDate) && isSameDay(rTo, endDate);
    });

    return (
        <div className={styles.container}>
            {/* Mode toggler */}
            {activeModes.length > 1 && (
                <div className={styles.modeToggler}>
                    <div
                        className={styles.modeRail}
                        style={{ '--index': modeIndex, '--count': activeModes.length } as RailVars}
                    >
                        <div className={styles.modeSlider} aria-hidden="true" />
                        {activeModes.map((m) => (
                            <button
                                key={m}
                                type="button"
                                className={[
                                    styles.modeTab,
                                    mode === m ? styles.modeTabActive : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleModeChange(m)}
                            >
                                {mergedLabels[m]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Date mode: calendar + presets */}
            {mode === 'date' && (
                <>
                    <div className={styles.calendar}>
                        <div className={styles.header}>
                            <button type="button" className={styles.navBtn} onClick={previousMonth}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <span className={styles.monthYear}>
                                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                            </span>
                            <button type="button" className={styles.navBtn} onClick={nextMonth}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </div>

                        <div className={styles.weekDays}>
                            {weekDays.map(d => <span key={d} className={styles.weekDay}>{d}</span>)}
                        </div>

                        <div className={styles.daysGrid}>
                            {daysInMonth.map((date, i) => {
                                if (!date) return <span key={`empty-${i}`} className={styles.emptyCell} />;

                                const selected = isSameDay(date, startDate) || isSameDay(date, endDate);
                                const inRange = isInRange(date);
                                const today = isToday(date);
                                const isRangeStart = isSameDay(date, startDate);
                                const isRangeEnd = isSameDay(date, endDate) || (phase === 'end' && isSameDay(date, hoveredDate));
                                const isSingleDay = isRangeStart && isRangeEnd;

                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        className={[
                                            styles.day,
                                            selected ? styles.daySelected : '',
                                            inRange && !selected ? styles.dayInRange : '',
                                            today ? styles.dayToday : '',
                                            isSingleDay ? styles.dayRangeSingle : '',
                                            !isSingleDay && isRangeStart && endDate ? styles.dayRangeStart : '',
                                            !isSingleDay && isRangeEnd ? styles.dayRangeEnd : '',
                                        ].filter(Boolean).join(' ')}
                                        onClick={() => handleDayClick(date)}
                                        onMouseEnter={() => phase === 'end' && setHoveredDate(date)}
                                        onMouseLeave={() => setHoveredDate(null)}
                                    >
                                        {date.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.presets}>
                        {activePresets.map((preset, i) => (
                            <button
                                key={i}
                                type="button"
                                className={[
                                    styles.presetBtn,
                                    i === activePresetIndex ? styles.presetBtnActive : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handlePreset(preset)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {/* Months mode: month counter */}
            {mode === 'months' && (
                <div className={styles.monthsMode}>
                    <span className={styles.monthsDescription}>{t('monthsDescription')}</span>
                    <div className={styles.monthsCounter}>
                        <button
                            type="button"
                            className={styles.monthsBtn}
                            onClick={() => handleMonthCountChange(monthCount - 1)}
                            disabled={monthCount <= 1}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                        <div className={styles.monthsValue}>
                            <span className={styles.monthsNumber}>{monthCount}</span>
                            <span className={styles.monthsUnit}>{t('monthsUnit')}</span>
                        </div>
                        <button
                            type="button"
                            className={styles.monthsBtn}
                            onClick={() => handleMonthCountChange(monthCount + 1)}
                            disabled={monthCount >= 24}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* All Time mode */}
            {mode === 'alltime' && (
                <div className={styles.alltimeMode}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8a3ffc" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span className={styles.alltimeText}>{t('allTimeDescription')}</span>
                </div>
            )}

            {/* Unified range display */}
            <div className={styles.rangeDisplay}>
                {mode === 'alltime'
                    ? t('allTime')
                    : startDate && endDate
                        ? `${formatDisplayDate(startDate)} → ${formatDisplayDate(endDate)}`
                        : '—'}
            </div>
        </div>
    );
};

function formatDisplayDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export default DateRangePicker;
